/**
 * ProcessPicker — React replacement for `lib/pilot/story-render.js`'s
 * `buildPickerCards` (R1 in the imperative-renderer retirement plan;
 * see `docs/eng/pilot-engine-imperative-retirement.md`).
 *
 * Three callsites consume this component:
 *
 *   - Story scene 2's `#story-cards` (the primary picker)
 *   - The "try another process" modal (`PilotChangeProcessModal`)
 *   - Sandbox's `#cards` (when no process is loaded; same picker re-rendered
 *     to let the visitor pick without going back to Story)
 *
 * Each callsite passes its own `onPick` + `onCustom` handlers; the
 * component takes the catalog (`processes`), the intake filter, and
 * an optional `className` so the grid sizing can vary per host.
 *
 * The custom-process card sits at the end of the grid and is always
 * present; it opens the Claude-generation flow.
 */

import React, {useMemo} from 'react';
import {clsx} from 'clsx/lite';
import {filterProcesses, type ProcessDef} from '../../lib/pilot/processes';
import type {Intake} from '../../lib/pilot/processes';
import {categoryGlyphSvg} from '../../lib/pilot/categoryGlyph';

export interface ProcessPickerProps {
  processes: Record<string, ProcessDef>;
  intake: Intake;
  onPick: (proc: ProcessDef) => void;
  onCustom: () => void;
  className?: string;
}

export default function ProcessPicker({
  processes,
  intake,
  onPick,
  onCustom,
  className,
}: ProcessPickerProps) {
  const items = useMemo(
    () => filterProcesses(processes, intake),
    [processes, intake],
  );
  return (
    <div
      className={clsx(
        'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((p) => (
        <ProcessCard key={p.id} proc={p} onClick={() => onPick(p)} />
      ))}
      <CustomProcessCard onClick={onCustom} />
    </div>
  );
}

/* ============================================================
   ProcessCard — a single process tile in the picker grid.
   ============================================================ */
function ProcessCard({proc, onClick}: {proc: ProcessDef; onClick: () => void}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-3 rounded-2xl border border-taupe-200 bg-white p-5 text-left text-taupe-950 transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-taupe-950 hover:shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_rgba(0,0,0,0.05)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-taupe-950 focus-visible:outline-offset-2 dark:border-taupe-800 dark:bg-taupe-900 dark:text-white dark:hover:border-taupe-200"
    >
      <CategoryGlyph category={proc.category} size={28} />
      <h3 className="font-display text-xl/7 font-normal tracking-tight text-taupe-950 dark:text-white">
        {proc.name}
      </h3>
      <p className="text-sm/6 text-taupe-700 dark:text-taupe-400">
        {proc.short || proc.desc}
      </p>
      {(proc.pills || []).length > 0 && (
        <div className="mt-auto flex flex-wrap gap-1.5">
          {(proc.pills || []).map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-full border border-taupe-200 bg-taupe-50 px-2 py-0.5 text-[11px] font-medium tracking-wide text-taupe-700 dark:border-taupe-700 dark:bg-taupe-800 dark:text-taupe-300"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

/* ============================================================
   CustomProcessCard — dashed-border tile that opens the
   Claude-generation flow.
   ============================================================ */
function CustomProcessCard({onClick}: {onClick: () => void}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col gap-3 rounded-2xl border border-dashed border-taupe-300 bg-taupe-50 p-5 text-left text-taupe-950 transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-taupe-950 hover:shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_rgba(0,0,0,0.05)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-taupe-950 focus-visible:outline-offset-2 dark:border-taupe-700 dark:bg-taupe-900 dark:text-white dark:hover:border-taupe-200"
    >
      <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-taupe-600 dark:text-taupe-400">
        Don't see yours?
      </div>
      <div className="inline-flex size-12 items-center justify-center rounded-xl border border-taupe-200 bg-taupe-100 dark:border-taupe-700 dark:bg-taupe-800">
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M 9 3 H 15 V 9 H 21 V 15 H 15 V 21 H 9 V 15 H 3 V 9 H 9 Z"
            className="fill-taupe-950 dark:fill-taupe-100"
          />
        </svg>
      </div>
      <h3 className="font-display text-xl/7 font-normal tracking-tight text-taupe-950 dark:text-white">
        Describe your own
      </h3>
      <p className="text-sm/6 text-taupe-700 dark:text-taupe-400">
        In plain English. Our team builds a tailored simulation around
        your real operation and sends it back.
      </p>
      <div className="mt-auto flex flex-wrap gap-1.5">
        <span className="inline-flex items-center rounded-full border border-taupe-200 bg-taupe-50 px-2 py-0.5 text-[11px] font-medium tracking-wide text-taupe-700 dark:border-taupe-700 dark:bg-taupe-800 dark:text-taupe-300">
          Same-day turnaround
        </span>
      </div>
    </button>
  );
}

/* ============================================================
   CategoryGlyph — boxed icon tile. Wraps the SVG from
   `lib/pilot/categoryGlyph.ts` in a Tailwind-styled container
   (replaces the legacy `.pp-cat-glyph` CSS rule, which stays in
   pilot-engine.css for the engine-emitted variant on Story scene 3
   header until R2 of the imperative-renderer retirement migrates
   that callsite too).
   ============================================================ */
function CategoryGlyph({
  category,
  size,
}: {
  category: string | undefined;
  size: number;
}) {
  return (
    <div
      className="inline-flex size-12 items-center justify-center rounded-xl border border-taupe-200 bg-taupe-100 dark:border-taupe-700 dark:bg-taupe-800"
      // The categoryGlyph module emits an <svg> string; React doesn't
      // own its internals (the SVG comes from a pure compute helper).
      // dangerouslySetInnerHTML is the appropriate primitive here.
      dangerouslySetInnerHTML={{__html: categoryGlyphSvg(category || '', size)}}
    />
  );
}
